import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { SchoolDocumentsManager } from "components/admin";
import { useAuth } from "context/auth-context";
import { workspaceApi } from "pages/app/workspace/services";
import { SchoolPlayerProfileTabProps } from "pages/app/workspace/school-player-profile/school-player-profile.types";
import { hasAnyPortalAction } from "utils/portal-access";

export const SchoolPlayerDocumentsTab: React.FC<SchoolPlayerProfileTabProps> = ({
  linkedSports,
  linkedTeams,
  playerId,
  schoolId,
  schoolPlayer,
}) => {
  const { portalAccess } = useAuth();

  const documentsQuery = useQuery({
    queryKey: ["school-player-profile-documents", schoolId],
    queryFn: () => workspaceApi.schoolDocuments.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const playerDocuments = useMemo(
    () =>
      (documentsQuery.data || []).filter(
        (document: any) => String(document.player_id || "") === String(playerId),
      ),
    [documentsQuery.data, playerId],
  );

  const canManageSchoolDocuments = hasAnyPortalAction(portalAccess, [
    "school_documents.manage",
    "school_documents.create",
    "school_documents.update",
    "school_documents.delete",
    "schools.update",
    "schools.manage",
    "school_matches.manage",
    "school_teams.update",
    "school_team_players.manage",
    "school_players.create",
  ]);
  const canVerifySchoolDocuments = hasAnyPortalAction(portalAccess, [
    "school_documents.verify",
    "school_documents.manage",
    "schools.update",
    "schools.manage",
    "school_matches.manage",
  ]);

  const documentInvalidateKeys = [
    ["school-player-profile-documents", schoolId],
    ["workspace-school-documents", schoolId],
  ].filter((queryKey) => Boolean(queryKey[1]));

  return (
    <SchoolDocumentsManager
      canManage={canManageSchoolDocuments}
      canVerify={canVerifySchoolDocuments}
      createLabel="Upload player document"
      documents={playerDocuments}
      emptyDescription="Player-scoped documents will appear here once they are uploaded."
      invalidateKeys={documentInvalidateKeys}
      isLoading={documentsQuery.isLoading}
      onCreate={(payload) =>
        workspaceApi.schoolDocuments.create(schoolId, {
          ...payload,
          player_id: playerId,
        })
      }
      onRemove={(document) => workspaceApi.schoolDocuments.remove(String(document.id))}
      onUpdate={(document, payload) =>
        workspaceApi.schoolDocuments.update(String(document.id), {
          ...payload,
          player_id: playerId,
        })
      }
      onVerify={(document) => workspaceApi.schoolDocuments.verify(String(document.id))}
      players={schoolPlayer ? [schoolPlayer] : []}
      sports={linkedSports}
      subtitle="Keep consent packs, medical documents, scouting notes, and school-facing records grouped on the player."
      teams={linkedTeams}
      title="Player Documents"
    />
  );
};
